/**
 * front-desk-pages.ts
 *
 * The pages the front desk may open — the hotel's receptionists, who hold a
 * read-only grant of the kind `front_desk` (`read-only-access.tsx`). They read
 * a property's prices, its reservations and why each price is what it is, and
 * nothing that states what the property earns, so they get the overview, the
 * Prices page, their own properties and their own account.
 *
 * Of the Prices page they get the reservations grid alone — the rooms, their
 * bookings and the price of each night, which is what a receptionist checks —
 * and not its other calendar views, its Price history tab or its Upload Log
 * (Oct 2026). The page is still theirs whatever an address names
 * (`isFrontDeskPage`): a tab it names is corrected in place
 * (`frontDeskPricesRoute`), since bouncing a bookmark of the Upload Log to the
 * overview would take them further from the prices than the grid is.
 *
 * It is an allow-list, never a refusal list: a page added later stays shut to
 * the front desk until somebody lists it here, which is the half of the rule
 * the server holds too (`optivo-service/src/middleware/readOnlyAccess.ts`
 * refuses the analytics reads to the front desk). The shell's verdict
 * (`useDashboardInit`), the rail and the overview's cards all ask this one
 * module, so no screen offers a page the shell would refuse.
 *
 * The account page's own sections are still decided where every reader's are
 * (`opensAccountSection`), and adding a property of their own is still the one
 * thing the settings page is for to a read-only reader.
 */

import type { PageName } from "../../navigation/pages";
import type { AppRoute } from "../../navigation/routes";

/** The pages the front desk may open, whatever the route names beyond the page. */
export const FRONT_DESK_PAGES: readonly PageName[] = ["dashboard", "drafts", "connections", "account"];

/**
 * Whether the front desk may open a page. A bare page name is the page
 * itself, so `settings` is refused there: only the route naming the
 * new-connection form opens it.
 */
export function isFrontDeskPage(target: PageName | AppRoute): boolean {
    const route: AppRoute = typeof target === "string" ? ({ page: target } as AppRoute) : target;
    if (route.page === "settings") return route.newConnection === true;
    return FRONT_DESK_PAGES.includes(route.page);
}

type PricesRoute = Extract<AppRoute, { page: "drafts" }>;

/**
 * Where an address of the Prices page is corrected to for the front desk, or
 * null where it already names nothing but the grid. The stay an address
 * brings is kept: it frames nights on the grid they are shown.
 */
export function frontDeskPricesRoute(route: PricesRoute): PricesRoute | null {
    if (route.tab == null || route.tab === "calendar") return null;
    return { page: "drafts", ...(route.stay ? { stay: route.stay } : {}) };
}

/**
 * Whether the overview offers the front desk a card. On top of the page being
 * theirs, only one card leads to the Prices page: the one that opens its
 * reservations grid. The other opens the page on whichever view was last read,
 * which for them is the same grid, under words about reviewing drafts before
 * they are sent.
 */
export function isFrontDeskCard(route: AppRoute): boolean {
    if (!isFrontDeskPage(route)) return false;
    return route.page !== "drafts" || route.calendarView === "reservations";
}
